// Marketing Hub - SEO Manager
// Module de gestion SEO avec données GSC réelles - Support Multi-Sites

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Search, TrendingUp, TrendingDown, Eye, MousePointer, Target,
  AlertTriangle, ArrowUpRight, ArrowDownRight, RefreshCw, Loader2,
  BarChart3, Lightbulb, ExternalLink, Filter, ChevronDown, Zap,
  Globe, FileText, Settings, CheckCircle, XCircle, Plus, X, EyeOff
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem("session_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// =============================================================================
// COMPONENTS
// =============================================================================

const KPICard = ({ title, value, variation, icon: Icon, color = "blue", suffix = "" }) => {
  const isPositive = variation >= 0;
  const colorClasses = {
    blue: "from-blue-500/20 to-blue-600/10 text-blue-400 border-blue-500/30",
    emerald: "from-emerald-500/20 to-emerald-600/10 text-emerald-400 border-emerald-500/30",
    amber: "from-amber-500/20 to-amber-600/10 text-amber-400 border-amber-500/30",
    purple: "from-purple-500/20 to-purple-600/10 text-purple-400 border-purple-500/30"
  };

  return (
    <Card className={`bg-zinc-900/50 border-l-2 ${colorClasses[color].split(' ').pop()} overflow-hidden`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-xs text-zinc-500 uppercase tracking-wider">{title}</p>
            <p className="text-2xl font-bold text-white mt-1">
              {typeof value === 'number' ? value.toLocaleString('fr-FR') : value}
              {suffix && <span className="text-sm font-normal text-zinc-400 ml-1">{suffix}</span>}
            </p>
          </div>
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
        {variation !== undefined && (
          <div className={`flex items-center gap-1 mt-2 text-xs ${isPositive ? 'text-emerald-400' : 'text-rose-400'}`}>
            {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            <span>{isPositive ? '+' : ''}{variation}%</span>
            <span className="text-zinc-500 ml-1">vs période précédente</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const OpportunityCard = ({ opportunity, onHide }) => {
  const priorityColors = {
    high: "bg-rose-500/20 text-rose-400 border-rose-500/30",
    medium: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    low: "bg-blue-500/20 text-blue-400 border-blue-500/30"
  };

  const typeIcons = {
    low_ctr: <MousePointer className="w-4 h-4" />,
    position_improvement: <TrendingUp className="w-4 h-4" />
  };

  const typeLabels = {
    low_ctr: "CTR à optimiser",
    position_improvement: "Potentiel de ranking"
  };

  return (
    <Card className="bg-zinc-900/50 border-white/10 hover:border-white/20 transition-colors group">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Badge className={`${priorityColors[opportunity.priority]} text-xs`}>
                {opportunity.priority === 'high' ? 'Haute priorité' : 'Priorité moyenne'}
              </Badge>
              <Badge variant="outline" className="text-xs flex items-center gap-1">
                {typeIcons[opportunity.type]}
                {typeLabels[opportunity.type]}
              </Badge>
            </div>
            <p className="text-white font-medium truncate" title={opportunity.query}>
              "{opportunity.query}"
            </p>
            <p className="text-xs text-zinc-400 mt-1">{opportunity.reason}</p>
          </div>
          <div className="text-right flex-shrink-0 flex items-start gap-2">
            <div>
              <p className="text-emerald-400 font-bold">+{opportunity.potential_clicks}</p>
              <p className="text-xs text-zinc-500">clics potentiels</p>
            </div>
            {onHide && (
              <button
                onClick={(e) => { e.stopPropagation(); onHide(opportunity.query); }}
                className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-zinc-900/50/10 text-zinc-500 hover:text-zinc-300 transition-all"
                title="Masquer cette opportunité"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4 mt-3 text-xs text-zinc-400">
          <span className="flex items-center gap-1">
            <Eye className="w-3 h-3" />
            {opportunity.impressions.toLocaleString('fr-FR')}
          </span>
          <span className="flex items-center gap-1">
            <MousePointer className="w-3 h-3" />
            {opportunity.clicks}
          </span>
          <span>CTR: {opportunity.ctr}%</span>
          <span>Pos: {opportunity.position}</span>
        </div>
      </CardContent>
    </Card>
  );
};

const DataTable = ({ data, columns, emptyMessage = "Aucune donnée" }) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-zinc-500">
        <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto oh-responsive-table">
      <table className="w-full">
        <thead>
          <tr className="border-b border-white/10">
            {columns.map((col, i) => (
              <th key={i} className={`text-left text-xs text-zinc-400 py-3 px-3 font-medium ${col.align === 'right' ? 'text-right' : ''}`}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr key={rowIndex} className="border-b border-white/5 hover:bg-zinc-900/50/5 transition-colors">
              {columns.map((col, colIndex) => (
                <td key={colIndex} className={`py-3 px-3 ${col.align === 'right' ? 'text-right' : ''}`}>
                  {col.render ? col.render(row[col.key], row) : (
                    <span className={col.className || 'text-zinc-300'}>
                      {typeof row[col.key] === 'number' ? row[col.key].toLocaleString('fr-FR') : row[col.key]}
                    </span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const SEOManager = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [overview, setOverview] = useState(null);
  const [queries, setQueries] = useState([]);
  const [pages, setPages] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [trends, setTrends] = useState([]);
  const [period, setPeriod] = useState(30);
  const [activeTab, setActiveTab] = useState("overview");
  const [showHiddenOpportunities, setShowHiddenOpportunities] = useState(false);
  
  // Multi-sites support
  const [gscConnections, setGscConnections] = useState([]);
  const [selectedSite, setSelectedSite] = useState("all"); // "all" ou site_url spécifique
  
  // GA4 SEO Data (trafic organique uniquement)
  const [ga4Organic, setGa4Organic] = useState(null);
  const [ga4Loading, setGa4Loading] = useState(false);
  const [ga4Connected, setGa4Connected] = useState(false);

  // Charger les données GA4 SEO (trafic organique)
  const loadGA4OrganicData = useCallback(async (siteUrl = null) => {
    setGa4Loading(true);
    try {
      const headers = getAuthHeaders();
      const siteParam = siteUrl && siteUrl !== "all" ? `&site_url=${encodeURIComponent(siteUrl)}` : "";
      
      // Test connexion GA4
      const testRes = await axios.get(`${API}/api/ga4/test`, { headers });
      setGa4Connected(testRes.data.success);
      
      if (testRes.data.success) {
        // Récupérer les données avec filtre organique et site_url pour mapping propriété
        const [overviewRes, sourcesRes, pagesRes] = await Promise.all([
          axios.get(`${API}/api/ga4/overview?start_date=30daysAgo&end_date=today${siteParam}`, { headers }),
          axios.get(`${API}/api/ga4/traffic-sources?limit=10${siteParam}`, { headers }),
          axios.get(`${API}/api/ga4/top-pages?limit=20${siteParam}`, { headers }).catch(() => ({ data: { pages: [] } }))
        ]);
        
        // Filtrer pour ne garder que l'organique
        const organicSources = (sourcesRes.data.sources || []).filter(s => 
          s.source?.toLowerCase().includes('google') || 
          s.source?.toLowerCase().includes('organic') ||
          s.source?.toLowerCase().includes('bing') ||
          s.source?.toLowerCase().includes('yahoo')
        );
        
        // Calculer les métriques organiques
        const totalSessions = sourcesRes.data.sources?.reduce((acc, s) => acc + (s.sessions || 0), 0) || 1;
        const organicSessions = organicSources.reduce((acc, s) => acc + (s.sessions || 0), 0);
        const organicShare = ((organicSessions / totalSessions) * 100).toFixed(1);
        
        setGa4Organic({
          overview: overviewRes.data,
          organicSources,
          organicSessions,
          organicShare,
          totalSessions,
          topPages: pagesRes.data.pages || []
        });
      }
    } catch (error) {
      console.error("Erreur GA4 SEO:", error);
      setGa4Connected(false);
    } finally {
      setGa4Loading(false);
    }
  }, []);

  // Charger les connexions GSC disponibles
  const loadGSCConnections = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/api/marketing/seo/sites`, { headers });
      setGscConnections(res.data.sites || []);
      
      // Si un seul site, le sélectionner automatiquement
      if (res.data.sites?.length === 1) {
        setSelectedSite(res.data.sites[0].site_url);
      }
    } catch (error) {
      console.error("Erreur chargement sites GSC:", error);
    }
  }, []);

  const fetchAllData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const headers = getAuthHeaders();
      const siteParam = selectedSite && selectedSite !== "all" ? `&site_url=${encodeURIComponent(selectedSite)}` : "";
      const hiddenParam = showHiddenOpportunities ? "&include_hidden=true" : "";

      // Fetch en parallèle
      const [statusRes, overviewRes, queriesRes, pagesRes, opportunitiesRes, trendsRes] = await Promise.all([
        axios.get(`${API}/api/marketing/seo/status${siteParam ? '?' + siteParam.slice(1) : ''}`, { headers }),
        axios.get(`${API}/api/marketing/seo/overview?days=${period}${siteParam}`, { headers }),
        axios.get(`${API}/api/marketing/seo/queries?days=${period}&limit=100${siteParam}`, { headers }),
        axios.get(`${API}/api/marketing/seo/pages?days=${period}&limit=50${siteParam}`, { headers }),
        axios.get(`${API}/api/marketing/seo/opportunities?days=${period}${siteParam}${hiddenParam}`, { headers }),
        axios.get(`${API}/api/marketing/seo/trends?days=${period}${siteParam}`, { headers })
      ]);

      setConnectionStatus(statusRes.data);
      setOverview(overviewRes.data);
      setQueries(queriesRes.data.queries || []);
      setPages(pagesRes.data.pages || []);
      setOpportunities(opportunitiesRes.data.opportunities || []);
      setTrends(trendsRes.data.trends || []);
    } catch (error) {
      console.error("Erreur chargement données SEO:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period, selectedSite, showHiddenOpportunities]);

  // Masquer une opportunité
  const hideOpportunity = async (query) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/api/marketing/seo/opportunities/hide`, { query }, { headers });
      // Retirer l'opportunité de la liste
      setOpportunities(prev => prev.filter(o => o.query !== query));
    } catch (error) {
      console.error("Erreur masquage opportunité:", error);
      alert("Erreur lors du masquage de l'opportunité");
    }
  };

  useEffect(() => {
    loadGSCConnections();
  }, [loadGSCConnections]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Recharger GA4 quand le site change
  useEffect(() => {
    loadGA4OrganicData(selectedSite);
  }, [selectedSite, loadGA4OrganicData]);

  const isMocked = overview?.mock || false;

  // Colonnes pour les tableaux
  const queryColumns = [
    {
      key: 'query',
      label: 'Requête',
      render: (val) => (
        <span className="text-white font-medium truncate block max-w-[300px]" title={val}>
          {val}
        </span>
      )
    },
    { key: 'clicks', label: 'Clics', align: 'right', className: 'text-emerald-400 font-medium' },
    { key: 'impressions', label: 'Impressions', align: 'right' },
    {
      key: 'ctr',
      label: 'CTR',
      align: 'right',
      render: (val) => <span className={val >= 5 ? 'text-emerald-400' : val >= 2 ? 'text-amber-400' : 'text-rose-400'}>{val}%</span>
    },
    {
      key: 'position',
      label: 'Position',
      align: 'right',
      render: (val) => <span className={val <= 3 ? 'text-emerald-400' : val <= 10 ? 'text-amber-400' : 'text-zinc-400'}>{val}</span>
    }
  ];

  const pageColumns = [
    {
      key: 'path',
      label: 'Page',
      render: (val, row) => (
        <div className="flex items-center gap-2 min-w-0">
          <Globe className="w-4 h-4 text-zinc-500 flex-shrink-0" />
          <a 
            href={row.url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-white font-medium hover:text-blue-400 transition-colors truncate"
            style={{ maxWidth: 'min(400px, 40vw)' }}
            title={row.url}
          >
            {val || '/'}
          </a>
          <ExternalLink className="w-3 h-3 text-zinc-500 flex-shrink-0 opacity-0 group-hover:opacity-100" />
        </div>
      )
    },
    { key: 'clicks', label: 'Clics', align: 'right', className: 'text-emerald-400 font-medium' },
    { key: 'impressions', label: 'Impressions', align: 'right' },
    {
      key: 'ctr',
      label: 'CTR',
      align: 'right',
      render: (val) => <span className={val >= 5 ? 'text-emerald-400' : 'text-amber-400'}>{val}%</span>
    },
    {
      key: 'position',
      label: 'Position moy.',
      align: 'right',
      render: (val) => <span className={val <= 5 ? 'text-emerald-400' : 'text-zinc-400'}>{val}</span>
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="seo-manager">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Search className="w-7 h-7 text-blue-400" />
            SEO Manager
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            Analysez vos performances SEO et identifiez les opportunités
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Status connexion */}
          <Badge className={connectionStatus?.connected ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}>
            {connectionStatus?.connected ? (
              <><CheckCircle className="w-3 h-3 mr-1" /> GSC Connecté</>
            ) : (
              <><XCircle className="w-3 h-3 mr-1" /> GSC Non connecté</>
            )}
          </Badge>
          
          {/* Sélecteur de site (multi-sites) */}
          {gscConnections.length > 0 && (
            <Select value={selectedSite} onValueChange={setSelectedSite}>
              <SelectTrigger className="w-full max-w-[220px] bg-zinc-800 border-white/10">
                <Globe className="w-4 h-4 mr-2 text-zinc-400" />
                <SelectValue placeholder="Sélectionner un site" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                {gscConnections.length > 1 && (
                  <SelectItem value="all" className="text-white">
                    Tous les sites ({gscConnections.length})
                  </SelectItem>
                )}
                {gscConnections.map((site) => (
                  <SelectItem key={site.site_url} value={site.site_url} className="text-white">
                    {site.name || site.site_url.replace('sc-domain:', '').replace('https://', '').replace('http://', '')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {/* Sélecteur période */}
          <select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
          >
            <option value={7}>7 derniers jours</option>
            <option value={30}>30 derniers jours</option>
            <option value={90}>90 derniers jours</option>
          </select>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchAllData(true)}
            disabled={refreshing}
            className="border-white/20"
          >
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="ml-2">Actualiser</span>
          </Button>
        </div>
      </div>

      {/* Alerte données mockées */}
      {isMocked && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-amber-400 font-medium">Données de démonstration</p>
            <p className="text-sm text-zinc-400 mt-1">
              Google Search Console n'est pas connecté. Les données affichées sont simulées.
              Pour connecter GSC, allez dans <strong className="text-white">Management Hub → Intégrations</strong> et ajoutez une connexion "Google Search Console".
            </p>
          </div>
        </div>
      )}

      {/* Indication source des données (Niveau 1 - Clarification visuelle) */}
      {!isMocked && selectedSite && selectedSite !== "all" && (
        <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-lg p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center text-lg">
            🔍
          </div>
          <div className="flex-1">
            <p className="text-base font-medium text-white">
              Analyse SEO du site : {selectedSite.replace("sc-domain:", "")}
            </p>
            <p className="text-sm text-zinc-400 mt-0.5">
              Visibilité, requêtes et opportunités (Search Console)
            </p>
          </div>
        </div>
      )}

      {/* KPIs Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Clics"
          value={overview?.data?.clicks?.value || 0}
          variation={overview?.data?.clicks?.variation}
          icon={MousePointer}
          color="emerald"
        />
        <KPICard
          title="Impressions"
          value={overview?.data?.impressions?.value || 0}
          variation={overview?.data?.impressions?.variation}
          icon={Eye}
          color="blue"
        />
        <KPICard
          title="CTR moyen"
          value={overview?.data?.ctr?.value || 0}
          variation={overview?.data?.ctr?.variation}
          icon={Target}
          color="purple"
          suffix="%"
        />
        <KPICard
          title="Position moyenne"
          value={overview?.data?.position?.value || 0}
          variation={overview?.data?.position?.variation}
          icon={BarChart3}
          color="amber"
        />
      </div>

      {/* Tabs - Structure claire : Search Console | Audience SEO | Opportunités */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-zinc-900/50 border border-white/10">
          <TabsTrigger value="overview" className="data-[state=active]:bg-zinc-900/50/10">
            <BarChart3 className="w-4 h-4 mr-2" />
            Vue d'ensemble
          </TabsTrigger>
          <TabsTrigger value="queries" className="data-[state=active]:bg-zinc-900/50/10">
            <Search className="w-4 h-4 mr-2" />
            Requêtes ({queries.length})
          </TabsTrigger>
          <TabsTrigger value="pages" className="data-[state=active]:bg-zinc-900/50/10">
            <FileText className="w-4 h-4 mr-2" />
            Pages ({pages.length})
          </TabsTrigger>
          <TabsTrigger value="audience" className="data-[state=active]:bg-zinc-900/50/10">
            <TrendingUp className="w-4 h-4 mr-2" />
            Audience SEO
            {ga4Connected && <span className="ml-1 w-2 h-2 rounded-full bg-emerald-400"></span>}
          </TabsTrigger>
          <TabsTrigger value="opportunities" className="data-[state=active]:bg-zinc-900/50/10">
            <Lightbulb className="w-4 h-4 mr-2" />
            Opportunités ({opportunities.length})
          </TabsTrigger>
        </TabsList>

        {/* Vue d'ensemble */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Requêtes */}
            <Card className="bg-zinc-900/50 border-white/10">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Search className="w-5 h-5 text-blue-400" />
                  Top Requêtes
                </CardTitle>
                <CardDescription>Les requêtes qui génèrent le plus de trafic</CardDescription>
              </CardHeader>
              <CardContent>
                <DataTable
                  data={queries.slice(0, 5)}
                  columns={queryColumns.slice(0, 3)}
                  emptyMessage="Aucune requête"
                />
              </CardContent>
            </Card>

            {/* Opportunités prioritaires */}
            <Card className="bg-zinc-900/50 border-white/10">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-400" />
                  Quick Wins
                </CardTitle>
                <CardDescription>Opportunités à fort potentiel</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {opportunities.slice(0, 3).map((opp, i) => (
                  <OpportunityCard key={i} opportunity={opp} onHide={hideOpportunity} />
                ))}
                {opportunities.length === 0 && (
                  <p className="text-center text-zinc-500 py-4">Aucune opportunité détectée</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Top Pages */}
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Globe className="w-5 h-5 text-emerald-400" />
                Pages les plus performantes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                data={pages.slice(0, 10)}
                columns={pageColumns}
                emptyMessage="Aucune page"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Requêtes */}
        <TabsContent value="queries">
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="w-5 h-5 text-blue-400" />
                Toutes les requêtes
              </CardTitle>
              <CardDescription>
                {queries.length} requêtes sur les {period} derniers jours
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                data={queries}
                columns={queryColumns}
                emptyMessage="Aucune requête trouvée"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pages */}
        <TabsContent value="pages">
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-400" />
                Toutes les pages
              </CardTitle>
              <CardDescription>
                {pages.length} pages sur les {period} derniers jours
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                data={pages}
                columns={pageColumns}
                emptyMessage="Aucune page trouvée"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Opportunités */}
        <TabsContent value="opportunities">
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-amber-400" />
                    Opportunités SEO
                  </CardTitle>
                  <CardDescription>
                    {opportunities.length} opportunités détectées - Requêtes avec fort potentiel d'amélioration
                  </CardDescription>
                </div>
                <Button
                  variant={showHiddenOpportunities ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowHiddenOpportunities(!showHiddenOpportunities)}
                  className={showHiddenOpportunities ? "bg-zinc-700" : ""}
                >
                  <EyeOff className="w-4 h-4 mr-2" />
                  {showHiddenOpportunities ? "Masquer les archivées" : "Voir les masquées"}
                </Button>
              </div>
              {/* Note importante sur le périmètre */}
              {selectedSite && selectedSite !== "all" && (
                <div className="mt-3 p-3 bg-zinc-800/50 rounded-lg border border-zinc-700/30">
                  <p className="text-xs text-zinc-400 flex items-center gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-400/70" />
                    <span>
                      Ces opportunités sont calculées à partir de la propriété GSC{" "}
                      <strong className="text-zinc-300">{selectedSite.replace("sc-domain:", "")}</strong>.
                      Elles peuvent inclure des requêtes liées à d'autres activités si elles apparaissent sur ce domaine.
                    </span>
                  </p>
                </div>
              )}
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {opportunities.map((opp, i) => (
                  <OpportunityCard key={i} opportunity={opp} onHide={hideOpportunity} />
                ))}
              </div>
              {opportunities.length === 0 && (
                <div className="text-center py-8 text-zinc-500">
                  <Lightbulb className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Aucune opportunité détectée pour cette période</p>
                  {!showHiddenOpportunities && (
                    <Button
                      variant="link"
                      className="text-zinc-400 mt-2"
                      onClick={() => setShowHiddenOpportunities(true)}
                    >
                      Voir les opportunités masquées
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audience SEO - Données GA4 filtrées sur le trafic organique */}
        <TabsContent value="audience" className="space-y-6">
          {ga4Loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
              <span className="ml-2 text-zinc-400">Chargement données Analytics...</span>
            </div>
          ) : !ga4Connected ? (
            <Card className="bg-zinc-900/50 border-white/10">
              <CardContent className="py-12 text-center">
                <TrendingUp className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">Google Analytics non connecté</h3>
                <p className="text-zinc-400 text-sm max-w-md mx-auto">
                  Connectez Google Analytics 4 pour voir les données d'audience SEO : 
                  sessions organiques, comportement des visiteurs, pages d'entrée.
                </p>
                <Button 
                  variant="outline" 
                  className="mt-4 border-purple-500/50 text-purple-400"
                  onClick={() => window.location.href = '/management/integrations'}
                >
                  Configurer GA4
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Note sur la source des données GA4 */}
              <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-lg p-4 flex items-center gap-4 mb-4">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center text-lg">
                  📊
                </div>
                <div className="flex-1">
                  <p className="text-base font-medium text-white">
                    Audience organique du site : {selectedSite && selectedSite !== "all" 
                      ? selectedSite.replace("sc-domain:", "") 
                      : "Non sélectionné"}
                  </p>
                  <p className="text-sm text-zinc-400 mt-0.5">
                    Comportement des visiteurs issus du SEO (Analytics)
                  </p>
                </div>
              </div>

              {/* KPIs Audience Organique */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-zinc-900/50 border-l-2 border-purple-500/30">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs text-zinc-500 uppercase tracking-wider">Sessions Organiques</p>
                        <p className="text-2xl font-bold text-white mt-1">
                          {ga4Organic?.organicSessions?.toLocaleString('fr-FR') || 0}
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-purple-400" />
                      </div>
                    </div>
                    <p className="text-xs text-zinc-500 mt-2">
                      {ga4Organic?.organicShare || 0}% du trafic total
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-zinc-900/50 border-l-2 border-emerald-500/30">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs text-zinc-500 uppercase tracking-wider">Utilisateurs Totaux</p>
                        <p className="text-2xl font-bold text-white mt-1">
                          {ga4Organic?.overview?.users?.toLocaleString('fr-FR') || 0}
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                        <Eye className="w-5 h-5 text-emerald-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-zinc-900/50 border-l-2 border-blue-500/30">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs text-zinc-500 uppercase tracking-wider">Taux de Rebond</p>
                        <p className="text-2xl font-bold text-white mt-1">
                          {ga4Organic?.overview?.bounce_rate?.toFixed(1) || 0}%
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                        <TrendingDown className="w-5 h-5 text-blue-400" />
                      </div>
                    </div>
                    <p className="text-xs text-zinc-500 mt-2">
                      {ga4Organic?.overview?.bounce_rate < 50 ? '✓ Bon' : 'À améliorer'}
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-zinc-900/50 border-l-2 border-amber-500/30">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs text-zinc-500 uppercase tracking-wider">Durée Moyenne</p>
                        <p className="text-2xl font-bold text-white mt-1">
                          {Math.floor((ga4Organic?.overview?.avg_session_duration || 0) / 60)}m {Math.round((ga4Organic?.overview?.avg_session_duration || 0) % 60)}s
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                        <BarChart3 className="w-5 h-5 text-amber-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Sources Organiques */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="bg-zinc-900/50 border-white/10">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Search className="w-5 h-5 text-purple-400" />
                      Sources de Recherche
                    </CardTitle>
                    <CardDescription>Moteurs de recherche qui vous apportent du trafic</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {ga4Organic?.organicSources?.length > 0 ? (
                      <div className="space-y-3">
                        {ga4Organic.organicSources.map((source, idx) => (
                          <div key={idx} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center">
                                <Globe className="w-4 h-4 text-zinc-400" />
                              </div>
                              <div>
                                <p className="text-white font-medium">{source.source}</p>
                                <p className="text-xs text-zinc-500">{source.engagement_rate?.toFixed(1)}% engagement</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-white font-medium">{source.sessions}</p>
                              <p className="text-xs text-zinc-500">sessions</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-zinc-500">
                        <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>Aucune donnée de recherche organique</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Résumé SEO */}
                <Card className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border-purple-500/20">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Zap className="w-5 h-5 text-purple-400" />
                      Résumé Performance SEO
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Synthèse croisée GSC + GA4 */}
                      <div className="p-4 bg-zinc-900/50 rounded-lg">
                        <h4 className="text-sm font-medium text-white mb-3">📊 Synthèse</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-zinc-400">Clics GSC → Sessions GA4</span>
                            <span className="text-white">
                              {overview?.data?.clicks?.value || 0} → {ga4Organic?.organicSessions || 0}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-zinc-400">Taux de conversion SEO</span>
                            <span className="text-emerald-400">
                              {overview?.data?.clicks?.value > 0 
                                ? ((ga4Organic?.organicSessions / overview?.data?.clicks?.value) * 100).toFixed(1)
                                : 0}%
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-zinc-400">Position moy. → Rebond</span>
                            <span className="text-white">
                              #{overview?.data?.position?.value?.toFixed(1) || '-'} → {ga4Organic?.overview?.bounce_rate?.toFixed(0) || '-'}%
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Indicateurs de santé */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="p-3 bg-zinc-900/50 rounded-lg text-center">
                          <div className={`text-2xl ${overview?.data?.ctr?.value >= 3 ? 'text-emerald-400' : 'text-amber-400'}`}>
                            {overview?.data?.ctr?.value >= 3 ? '✓' : '⚠'}
                          </div>
                          <p className="text-xs text-zinc-400 mt-1">CTR {overview?.data?.ctr?.value >= 3 ? 'Bon' : 'À améliorer'}</p>
                        </div>
                        <div className="p-3 bg-zinc-900/50 rounded-lg text-center">
                          <div className={`text-2xl ${ga4Organic?.overview?.bounce_rate < 60 ? 'text-emerald-400' : 'text-amber-400'}`}>
                            {ga4Organic?.overview?.bounce_rate < 60 ? '✓' : '⚠'}
                          </div>
                          <p className="text-xs text-zinc-400 mt-1">Rebond {ga4Organic?.overview?.bounce_rate < 60 ? 'Bon' : 'Élevé'}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SEOManager;
